import {
  useCurrentRefinements,
  useRefinementList,
  type UseRefinementListProps,
} from 'react-instantsearch'
import SearchRefinementListItem, { type SearchRefinementListItemProps } from './search-refinement-list-item'
import type { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { Link, Typography } from '@shc/ui'
import useScreenMediaQuery from '@hooks/useScreenMediaQuery'
import Accordion from './accordion'
import { useState } from 'react'
import { useCollapse } from 'react-collapsed'
import { hierarchicalDelimiter, transformItems } from '@lib/utilities/search'
export interface SearchRefinementListProps extends UseRefinementListProps {
  title: string,
  restrictedRegions: string[];
  disableSingleItemBehavior?: boolean;
}

const SearchRefinementList = ({ restrictedRegions, title, disableSingleItemBehavior = true, ...props }: SearchRefinementListProps) => {
  const { items, refine, canRefine, sendEvent } = useRefinementList({ 
    ...props,
    showMore: false,
    limit: Infinity,
    transformItems,
  })

  // this is here because of news2know since their results are based on region access
  const finalItemsUnsorted = (title === 'Regions' && restrictedRegions?.length !== 0) ? items.filter((item) => restrictedRegions?.includes(item.label)) : items;

  // Alphabetize display labels for Category and Region facets (UI-only sort).
  const shouldAlphabetize = ['categories', 'regions', 'facet_regions'].includes(String(props.attribute)) || title === 'Category' || title === 'Regions'
  const finalItems = shouldAlphabetize
    ? [...finalItemsUnsorted].sort((a, b) => String(a.label).localeCompare(String(b.label), undefined, { sensitivity: 'base' }))
    : finalItemsUnsorted

  const isDesktop = useScreenMediaQuery('xl')
  const currentRefinements = useCurrentRefinements()
  const [isShowingMore, setIsShowingMore] = useState(false)
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded: isShowingMore })
  const limit = props.limit ?? 6
  const canShowMore = finalItems.length > limit
  const limitedItems = canShowMore ? finalItems.slice(0, limit) : finalItems
  const moreItems = canShowMore ? finalItems.slice(limit) : []

  const onChangeHandler = (
    value: string,
    hasChildRefined: boolean,
    data?: SearchRefinementListItemProps[]
  ): void => {
    sendEvent('click', value)

    if (!hasChildRefined) refine(value)
    // find other refinements for this facet
    const otherRefinements =
      currentRefinements.items.find(({ attribute }) => attribute === props.attribute)
        ?.refinements ?? []

    // for each other refinement look for a parent and unrefine it
    otherRefinements.forEach((otherRefinement) => {
      const refinementParts = value.toString().split(hierarchicalDelimiter)
      if (
        typeof otherRefinement.value === 'string' &&
        otherRefinement.value ===
          refinementParts.slice(0, refinementParts.length - 1).join(hierarchicalDelimiter)
      )
        currentRefinements.refine(otherRefinement)
    })

    // deselect children refinements
    data?.forEach((item) => {
      if (item.isRefined) refine(item.value)
    })
  }

  const toggleAccordion = () => {
    const containerElementTarget = document.querySelector<HTMLElement>(
      "[data-search='results-container']"
    )
    const isScrollPassContainerElement =
      containerElementTarget && window.scrollY >= containerElementTarget.offsetTop

    if (!isScrollPassContainerElement) {
      containerElementTarget?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    setIsShowingMore((isShowingMore) => !isShowingMore)
  }

  const itemMapper = (item: RefinementListItem) => (
    <SearchRefinementListItem {...item} key={item.value} onChange={onChangeHandler} isDisabled={false}/>
  )

  if (!canRefine) return null

  if (isDesktop) {
    return (
      <fieldset className="border-b pb-5">
        <legend className="pt-5 pb-3">
          <Typography variant="body-semibold">{title}</Typography>
        </legend>
        <div className="[&>*:first-child]:pt-0">
          {finalItems.length === 1 ? (
            <SearchRefinementListItem {...finalItems[0]} key={finalItems[0].value} onChange={onChangeHandler} isRefined={finalItems[0].isRefined} isDisabled={disableSingleItemBehavior}/>
          ) : (
            <>
              {limitedItems.map(itemMapper)}
              <div {...getCollapseProps()}>{moreItems.map(itemMapper)}</div>
              {canShowMore && (
                <Link
                  as="button"
                  noUnderline
                  className="text-sm mt-3 text-tertiary"
                  {...getToggleProps({ onClick: toggleAccordion })}>
                  See {isShowingMore ? 'less' : 'more'}
                </Link>
              )}
            </>
          )}
        </div>
      </fieldset>
    )
  }

  return (
    <Accordion title={title} className="search-filters">
      <div className="[&>*:first-child]:pt-0">
          {finalItems.length === 1 ? (
            <SearchRefinementListItem {...finalItems[0]} key={finalItems[0].value} onChange={onChangeHandler} isRefined={finalItems[0].isRefined} isDisabled={disableSingleItemBehavior}/>
          ) : finalItems.map(itemMapper)
          }
        </div>
    </Accordion>
  )
}

export default SearchRefinementList
